"use client";

import clsx from "clsx";
import {useEffect, useRef} from "react";
import {useTranslation} from "react-i18next";
import Icon, {ICON_SIZES} from "@/components/library/Icon";
import {PopoverContent} from "@/components/ui/popover";
import type {useSlashCommandMenu} from "./useSlashCommandMenu";

type SlashCommandMenuProps = {
   menu: ReturnType<typeof useSlashCommandMenu>;
   isLoading: boolean;
   side: "top" | "bottom";
   containerRef: React.RefObject<HTMLDivElement | null>;
};

export default function SlashCommandMenu({menu, isLoading, side, containerRef}: SlashCommandMenuProps) {
   const {t} = useTranslation("ask");
   const activeRef = useRef<HTMLButtonElement>(null);

   useEffect(() => {
      activeRef.current?.scrollIntoView?.({block: "nearest"});
   }, [menu.selectedIndex, menu.isOpen, menu.matches]);

   return (
      <PopoverContent
         side={side}
         align="start"
         sideOffset={8}
         collisionPadding={12}
         role="presentation"
         className="flex max-h-[min(380px,var(--radix-popover-content-available-height))] w-[var(--radix-popover-trigger-width)] flex-col overflow-hidden rounded-[20px] border-neutral/70 bg-background p-1.5 text-900 shadow-[0_8px_30px_rgba(0,0,0,0.10)] dark:shadow-[0_8px_30px_rgba(0,0,0,0.3)]"
         onOpenAutoFocus={(event) => event.preventDefault()}
         onCloseAutoFocus={(event) => event.preventDefault()}
         onInteractOutside={(event) => {
            if (containerRef.current?.contains(event.target as Node)) event.preventDefault();
         }}
      >
         <div id={`${menu.listboxId}-label`} className="px-3 pb-1.5 pt-2 text-xs font-medium text-500 dark:text-600">
            {t("slashCommands.title")}
         </div>
         <div
            id={menu.listboxId}
            role="listbox"
            aria-labelledby={`${menu.listboxId}-label`}
            aria-busy={isLoading}
            className="min-h-0 overflow-y-auto overscroll-contain"
         >
            {menu.matches.map((skill, index) => (
               <button
                  key={skill.id}
                  ref={index === menu.selectedIndex ? activeRef : undefined}
                  id={menu.getOptionId(index)}
                  type="button"
                  role="option"
                  aria-selected={index === menu.selectedIndex}
                  aria-labelledby={`${menu.getOptionId(index)}-title`}
                  aria-describedby={`${menu.getOptionId(index)}-description`}
                  tabIndex={-1}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseMove={() => menu.setActiveIndex(index)}
                  onClick={() => menu.selectSkill(skill)}
                  className={clsx(
                     "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors duration-100",
                     index === menu.selectedIndex
                        ? "bg-blue-50 ring-1 ring-inset ring-blue-200 dark:bg-blue-950 dark:ring-blue-800"
                        : "hover:bg-100 dark:hover:bg-200"
                  )}
               >
                  <Icon icon={skill.icon} size={ICON_SIZES.BASE} className="h-4 w-4 shrink-0 text-600" aria-hidden="true" />
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-2.5">
                     <span id={`${menu.getOptionId(index)}-title`} className="shrink-0 text-sm font-medium text-900">
                        {skill.title}
                     </span>
                     <span id={`${menu.getOptionId(index)}-description`} className="truncate text-xs text-500 dark:text-600 sm:text-sm">
                        {skill.description}
                     </span>
                  </span>
                  {index === menu.selectedIndex && (
                     <span className="hidden shrink-0 text-sm text-500 sm:block" aria-hidden="true">↵</span>
                  )}
               </button>
            ))}
         </div>
         {(isLoading || menu.matches.length === 0) && (
            <p role="status" className="px-3 py-5 text-sm text-500 dark:text-600">
               {t(isLoading ? "status.loading_skills" : "slashCommands.noResults")}
            </p>
         )}
         <div className="mx-3 mt-1.5 hidden shrink-0 items-center gap-4 border-t border-neutral/50 pb-1.5 pt-2 text-[11px] text-500 dark:text-600 sm:flex">
            <span><kbd className="font-sans">↑ ↓</kbd> {t("slashCommands.navigate")}</span>
            <span><kbd className="font-sans">↵ / Tab</kbd> {t("slashCommands.select")}</span>
            <span className="ml-auto"><kbd className="font-sans">Esc</kbd> {t("slashCommands.dismiss")}</span>
         </div>
      </PopoverContent>
   );
}
